import * as Sentry from '@sentry/vue'
import ContentConvertApi from '@/api/contentConvert'
import EventBus from '@/eventBus'
import RightNavTabs from '@/constant/RightNavTabs'
import Config from '@/config'
import { CONVERTIBLE_SLIDE_TYPES } from '@/constant/contentV2Convert'

// AHA-47744: legacy content slide -> content-v2 slide, via the ahaaiv2
// converter (POST /api/ai-v2/content/convert-slide).
//
// This module is deliberately store-pure: no toasts, no i18n, no Mixpanel.
// `@/util` and `@/i18nPresenter` both import the store, and a Vuex module
// importing either closes an import cycle (store -> modules -> here -> store)
// that the repo's strict lint rejects for new files. The action therefore
// returns an outcome object and the contentV2Convert mixin does the talking.

export const CONVERT_OUTCOME = {
  OK: 'ok',
  // Nothing to do: placeholder slide, non-convertible type, or a conversion
  // already in flight. Silent by design.
  SKIPPED: 'skipped',
  SLIDE_LIMIT: 'slide_limit',
  FAILED: 'failed',
}

// Same settle delay the AI "add slide" path uses before syncing the
// left-panel selection to the new active slide.
const SELECT_SETTLE_MS = 250
const _wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms) })

const moduleState = {
  // Source slide id while a conversion is in flight; null when idle. Doubles
  // as the re-entrancy guard so a double click cannot create two slides.
  convertingSlideId: null,
}

const getters = {
  isConverting: (state) => state.convertingSlideId !== null,
  convertingSlideId: (state) => state.convertingSlideId,
}

const mutations = {
  /* eslint-disable no-param-reassign */
  SET_CONVERTING_SLIDE_ID(state, slideId) {
    state.convertingSlideId = slideId
  },
  /* eslint-enable no-param-reassign */
}

// HELPERS

const _isConvertible = (slideActive) => Boolean(slideActive?.id)
  && CONVERTIBLE_SLIDE_TYPES.includes(slideActive.type)

const _isAtSlideLimit = (presentation) => (presentation?.slides?.length || 0) >= Config.MAX_NUMBER_OF_SLIDE

// The converter returns only the id; the slide list needs the full row.
const _fetchCreatedSlide = async (newSlideId) => {
  const newSlide = await ContentConvertApi.getSlideById(newSlideId)
  if (!newSlide) {
    throw new Error(`convert-slide: created slide ${newSlideId} could not be fetched`)
  }
  return newSlide
}

// The API may already have pushed the new slide into this editor through the
// 'presentation: update-slides' socket refresh. Never insert twice.
const _insertAfterSource = async (dispatch, rootState, sourceSlideId, newSlide) => {
  const currentSlides = rootState.presentation.slides
  if (currentSlides.some((s) => s.id === newSlide.id)) return
  const slides = [...currentSlides]
  const sourceIndex = slides.findIndex((s) => s.id === sourceSlideId)
  const insertAt = sourceIndex === -1 ? slides.length : sourceIndex + 1
  slides.splice(insertAt, 0, newSlide)
  await dispatch('patchPresentationAction', { slides }, { root: true })
}

const _selectNewSlide = async (dispatch, newSlide) => {
  dispatch('setSlideActiveAction', newSlide, { root: true })
  await _wait(SELECT_SETTLE_MS)
  EventBus.$emit('SideLeft: setSelectedSlideIds', [newSlide.id])
  dispatch('rightNav/setRightNavActiveTabAction', RightNavTabs.TAB_NAMES.CONTENT, { root: true })
}

// Structured converter errors arrive as `body.detail = { code, message }`.
const _errorCodeOf = (error) => error?.body?.detail?.code || null

const _reportFailure = (error, code, { presentationId, slideId }) => {
  const reported = error instanceof Error
    ? error
    : new Error(`convert-slide failed with status ${error?.status ?? 'unknown'}${code ? ` (${code})` : ''}`)
  Sentry.captureException(reported, {
    tags: { feature: 'content-v2-convert', context: 'convert-active-slide-action' },
    extra: { presentationId, slideId, code },
  })
}

const actions = {
  /**
   * Converts one slide — `slideId` when given, otherwise the active slide —
   * inserts the new content-v2 slide after it and selects it. Resolves with
   * an outcome object:
   *   { status: 'ok', result, newSlide }
   *   { status: 'skipped' } | { status: 'slide_limit' }
   *   { status: 'failed', code }  — code is the converter's structured error
   *   code (e.g. 'unsupported_slide_type', 'empty_slide') or null.
   * Never rejects; the caller decides what the user sees.
   */
  async convertSlideToContentV2Action({
    state, commit, dispatch, rootState,
  }, { slideId } = {}) {
    const { slideActive, presentation } = rootState
    const source = slideId == null
      ? slideActive
      : (presentation?.slides || []).find((s) => s.id === slideId)
    if (!_isConvertible(source) || state.convertingSlideId !== null) {
      return { status: CONVERT_OUTCOME.SKIPPED }
    }
    if (_isAtSlideLimit(presentation)) {
      return { status: CONVERT_OUTCOME.SLIDE_LIMIT }
    }
    const sourceSlideId = source.id
    const presentationId = presentation.id

    commit('SET_CONVERTING_SLIDE_ID', sourceSlideId)
    try {
      const result = await ContentConvertApi.convertSlide({ presentationId, slideId: sourceSlideId })
      const newSlide = await _fetchCreatedSlide(result.new_slide_id)
      await _insertAfterSource(dispatch, rootState, sourceSlideId, newSlide)
      await _selectNewSlide(dispatch, newSlide)
      return { status: CONVERT_OUTCOME.OK, result, newSlide }
    } catch (error) {
      const code = _errorCodeOf(error)
      _reportFailure(error, code, { presentationId, slideId: sourceSlideId })
      return { status: CONVERT_OUTCOME.FAILED, code }
    } finally {
      commit('SET_CONVERTING_SLIDE_ID', null)
    }
  },
}

export default {
  namespaced: true,
  state: moduleState,
  getters,
  mutations,
  actions,
}
